import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { User } from '@app/auth/auth.models';
import { AuthService } from '@app/auth/services/auth/auth.service';
import { CRITICAL_STATS_IDS, CRITICAL_STATS_CARD_LABELS } from '@app/core/constants/critical-stats-constants';
import { ICriticalStatsCard } from '@app/core/models/critical-stats-card';
import { RcmRepairOrder } from '@app/shared/services/api/api.models';
import { ApiService } from '@app/shared/services/api/api.service';
import { CriticalStatsService } from '@app/shared/services/criticalstats.service';
import { LoadingService } from '@app/shared/services/loading/loading.service';
import { RcmService } from '@app/shared/services/rcm/rcm.service';
import { SharedService } from '@app/shared/services/shared.service';
import { REPAIRORDERLINE_STATUS_DESCRIPTIONS } from '@app/core/constants/globalConstants';
import { Subscription } from 'rxjs';
import { NO_VALUE_TEXT, RcmGridColumnDetails, RcmGridData } from '../rcm-data-table/rcm-data-table/rcm-data-table.component';

@Component({
  selector: 'aar-rcm-history',
  templateUrl: './rcm-history.component.html',
  styleUrls: ['./rcm-history.component.scss']
})

export class RcmHistoryComponent implements OnInit, OnDestroy {

  private repairOrders: RcmRepairOrder[] = [];
  private repairOrdersAux: RcmRepairOrder[] = [];
  private filterConfig = {
    active: false,
    filter: null
  };
  private currentUser: User;
  private criticalStatsIdClosed = CRITICAL_STATS_IDS.RO_CLOSED;
  private criticalStatsIdClosedDescription = REPAIRORDERLINE_STATUS_DESCRIPTIONS.orderClosed;
  private criticalStatsIdCanceledDescription = REPAIRORDERLINE_STATUS_DESCRIPTIONS.orderCanceled;
  private criticalStatsCards: ICriticalStatsCard[];
  private repairManagerStatsCards: ICriticalStatsCard[] = [
    { id: CRITICAL_STATS_IDS.RO_CLOSED, label: CRITICAL_STATS_CARD_LABELS.RO_CLOSED, isEnabled: true, value: 0 },
    { id: CRITICAL_STATS_IDS.RO_CANCELED, label: CRITICAL_STATS_CARD_LABELS.RO_CANCELED, isEnabled: true, value: 0 },
  ];
  private searchKeyword: string = "";
  private externalSubscriptions$ = new Subscription;
  /* Commented under #40361
  directShip = [];
  */
  rcmGridOrders: RcmGridData[] = [];
  columnsSettings: RcmGridColumnDetails[] = [];

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    private apiService: ApiService,
    private loadingService: LoadingService,
    private authService: AuthService,
    private rcmService: RcmService,
    private criticalStatsService: CriticalStatsService,
    private sharedService: SharedService) {
  }

  ngOnInit() {

    this.addExternalSubscriptions();
    this.getCurrentUser();
    this.setGridHeaders();
    this.getRepairOrders();
    this.criticalStatsCards = this.repairManagerStatsCards;
    this.loadingService.startLoading();
  }

  ngOnDestroy(): void {
    this.unsubscribeFromExternalEvents();
  }

  private addExternalSubscriptions() {

    this.externalSubscriptions$.add(this.rcmService.filterSelected$.subscribe(filterBy => this.filterByStatus(filterBy)));
    this.externalSubscriptions$.add(this.route.queryParams.subscribe(params => {
      if (params.filterBy) {
        this.filterByStatus(params.filterBy);
      }
    }));

    this.externalSubscriptions$.add(this.sharedService.getSearchKeyWord().subscribe((message) => {
      this.searchKeyword = message;

      this.filterByKeyWord();
      this.calculateCriticalStats(this.repairOrdersAux);
      this.filterByCards();
    }));
    this.externalSubscriptions$.add(this.sharedService.getObject().subscribe((filters) => {
      this.filterByKeyWord();
      this.calculateCriticalStats(this.repairOrdersAux);
      this.filterByCards();
    }));
  }
  private unsubscribeFromExternalEvents(): void {
    this.externalSubscriptions$.unsubscribe();
  }

  private getCurrentUser(): void {
    if (this.authService.isUserLoggedIn())
      this.currentUser = this.authService.getLoggedUser();

    this.externalSubscriptions$.add(this.authService.user$.subscribe((user: User) => {
      this.currentUser = user;
    }));
  }

  private setGridHeaders(): void {
    this.columnsSettings = [
      /* Commented under #40361
      { name: "poNumber", header: "Purchase Order" },
       */
      { name: 'repairOrderNumber', label: 'Repair Order', filterable: true, exportable: true, showInGrid: true },
      { name: 'programmeDescription', label: 'Programme', filterable: true, exportable: true, showInGrid: true },
      { name: 'partNumber', label: 'Part Number', filterable: true, exportable: true, showInGrid: true },
      { name: 'description', label: 'Description', filterable: true, exportable: true, showInGrid: true },
      { name: 'serialNumber', label: 'Serial Number', filterable: true, exportable: true, showInGrid: true },
      { name: 'workscopeName', label: 'Workscope', filterable: true, exportable: true, showInGrid: true },
      { name: 'customerAccountManager', label: 'User', filterable: true, exportable: true, showInGrid: true },
      { name: 'supplierName', label: 'Supplier', filterable: true, exportable: true, showInGrid: true },
      { name: 'repairLineStatus', label: 'Status', filterable: true, exportable: true, showInGrid: true },
      { name: 'priority', label: 'Priority', filterable: true, exportable: true, showInGrid: true },
      { name: 'nTat', label: 'Net TAT', filterable: true, exportable: true, showInGrid: true },
      { name: 'gTat', label: 'Gross TAT', filterable: true, exportable: true, showInGrid: true },
      { name: 'documents', label: 'Document(s)', filterable: true, exportable: true, showInGrid: true },
      { name: 'repairOrderLineId', label: 'RepairOrderLineId', filterable: true, exportable: false, showInGrid: false }
    ];
  }

  private getRepairOrders(): void {
    this.getCurrentUser();
    this.externalSubscriptions$.add(this.apiService.getRcmRepairOrders()
      .subscribe(repairOrders => {
        this.loadingService.stopLoading();
        this.repairOrders = repairOrders.map((order) => ({
          ...order,
          buyerName: (order.buyerName || '').toUpperCase(),
          customerAccountManager: (order.repairOrderLine.customerAccountManager || '').toUpperCase(),
          /* Commented under #40361
          directShip: order.repairOrderLine.directShip ? "YES" : "NO",
          */
          repairLineStatus: (order.repairOrderLine.repairLineStatus || '').toUpperCase(),
          partNumber: order.repairOrderLine.partNumber,
          serialNumber: order.repairOrderLine.serialNumber
        })) as any;
        this.repairOrdersAux = this.repairOrders.filter(x => x.repairOrderLine.repairLineStatusDescription === this.criticalStatsIdClosedDescription);
        this.calculateCriticalStats(this.repairOrders);
        this.buildGridData(this.repairOrdersAux);
      }, err => {
        this.loadingService.loadingError();
      }));
  }

  private buildGridData(orders: RcmRepairOrder[]): void {
    this.rcmGridOrders = orders.map((order) => ({
      repairOrderId: order.repairOrderId,
      repairOrderNumber: order.repairOrderNumber,
      supplierName: order.supplierName,
      programmeDescription: order.programmeDescription,
      repairOrderLineId: order.repairOrderLineId,
      nTat: order.repairOrderLine.nTat.toString(),
      description: order.repairOrderLine.description,
      gTat: order.repairOrderLine.gTat.toString(),
      partNumber: order.repairOrderLine.partNumber,
      priority: order.repairOrderLine.priority ? order.repairOrderLine.priority : NO_VALUE_TEXT,
      repairLineStatus: order.repairOrderLine.repairLineStatus,
      serialNumber: order.repairOrderLine.serialNumber ? order.repairOrderLine.serialNumber : NO_VALUE_TEXT,
      workscopeName: order.repairOrderLine.workscopeName,
      repairLineStatusDescription: order.repairOrderLine.repairLineStatusDescription,
      documents: order.repairOrderLine.hasDocuments ? 'Yes' : 'No',
      paperClipImage: 'assets/images/fpo/suppliers/paperclip.png',
      customerAccountManager: order.repairOrderLine.customerAccountManager ? order.repairOrderLine.customerAccountManager : NO_VALUE_TEXT
    } as RcmGridData));
  }

  private calculateCriticalStats(repairOrders: RcmRepairOrder[]): void {
    this.externalSubscriptions$.add(this.apiService.getCriticalStats()
      .subscribe(criticalstats => {
        const statuses = this.criticalStatsService.calculateHistoricCriticalStats(this.currentUser.roleName, criticalstats, repairOrders);
        this.criticalStatsCards.forEach(card => {
          card.value = statuses[card.id];
        });

        this.filterByCards();

      }, err => {
        console.log('Critical Stats Calculation error : ' + err);
      }));

  }

  private filterByStatus(filter: string = null): void {
    (filter == null) ?
      this.filterConfig.filter = null :
      this.filterConfig.filter = filter;

    this.filterConfig.active = true;

    this.filterByKeyWord();
    this.filterByCards();

  }

  private filterByKeyWord() {
    const message = this.searchKeyword || ''
    this.repairOrdersAux = this.repairOrders.filter(x => x.repairOrderLine.repairLineStatusDescription === this.criticalStatsIdClosedDescription ||
      x.repairOrderLine.repairLineStatusDescription === this.criticalStatsIdCanceledDescription);

    this.repairOrdersAux = this.repairOrdersAux.filter(
      (x) =>
        (x.repairOrderNumber &&
          x.repairOrderNumber.toLowerCase().includes(message.toLowerCase())) ||
        (x.repairOrderLine.partNumber &&
          x.repairOrderLine.partNumber.toLowerCase().includes(message.toLowerCase())) ||
        (x.repairOrderLine.description &&
          x.repairOrderLine.description.toLowerCase().includes(message.toLowerCase())) ||
        (x.repairOrderLine.serialNumber &&
          x.repairOrderLine.serialNumber.toLowerCase().includes(message.toLowerCase())) ||
        (x.repairOrderLine.workscopeName &&
          x.repairOrderLine.workscopeName.toLowerCase().includes(message.toLowerCase())) ||
        (x.repairOrderLine.customerAccountManager &&
          x.repairOrderLine.customerAccountManager.toLowerCase().includes(message.toLowerCase())) ||
        (x.repairOrderLine.repairLineStatus &&
          x.repairOrderLine.repairLineStatus.toLowerCase().includes(message.toLowerCase())) ||
        (x.repairOrderLine.priority &&
          x.repairOrderLine.priority.toLowerCase().includes(message.toLowerCase())) ||
        (x.supplierName &&
          x.supplierName.toLowerCase().includes(message.toLowerCase())))
  }

  private filterByCards(): void {
    if (this.filterConfig.active && this.filterConfig.filter !== null) {
      this.repairOrdersAux = (this.filterConfig.filter === this.criticalStatsIdClosed) ?
        this.repairOrdersAux.filter((ro) => this.criticalStatsIdClosedDescription.includes(ro.repairOrderLine.repairLineStatusDescription)) :
        this.repairOrdersAux.filter((ro) => this.criticalStatsIdCanceledDescription.includes(ro.repairOrderLine.repairLineStatusDescription));
    } else {
      this.filterConfig.active = true;
      this.filterConfig.filter = this.criticalStatsIdClosed;
    }
    this.buildGridData(this.repairOrdersAux);
  }

  isCardActive(cardId: string): boolean {
    return this.filterConfig.active && this.filterConfig.filter === cardId;
  }
}